import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, MapPin, Minus, Plus, Search, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useContent } from "@/lib/content";

function Counter({ label, sub, value, onChange, min = 0, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div>
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        <p className="text-xs text-slate-400">{sub}</p>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`Decrease ${label}`} onClick={() => onChange(Math.max(min, value - 1))}
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-royal hover:text-royal transition-colors">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span data-testid={testid} className="w-6 text-center text-sm font-bold text-navy">{value}</span>
        <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)}
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-royal hover:text-royal transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function SearchWidget() {
  const { destinations } = useContent();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [showSugg, setShowSugg] = useState(false);
  const [range, setRange] = useState(undefined);
  const [trav, setTrav] = useState({ adults: 2, children: 0, infants: 0 });
  const blurTimer = useRef(null);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return destinations.filter((d) => d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q)).slice(0, 6);
  }, [query, destinations]);

  const dateLabel = range?.from
    ? range.to
      ? `${format(range.from, "dd MMM")} — ${format(range.to, "dd MMM")}`
      : format(range.from, "dd MMM yyyy")
    : "Travel dates";

  const travLabel = `${trav.adults} Adult${trav.adults !== 1 ? "s" : ""}${trav.children ? `, ${trav.children} Child` : ""}${trav.infants ? `, ${trav.infants} Infant` : ""}`;

  const submit = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("destination", query.trim());
    if (range?.from) params.set("from", format(range.from, "yyyy-MM-dd"));
    if (range?.to) params.set("to", format(range.to, "yyyy-MM-dd"));
    params.set("adults", String(trav.adults));
    if (trav.children) params.set("children", String(trav.children));
    if (trav.infants) params.set("infants", String(trav.infants));
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div data-testid="hero-search-widget" className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-3 sm:p-4">
      <div className="grid gap-3 md:grid-cols-[1.4fr_1.2fr_1.1fr_auto]">
        <div className="relative">
          <div className="flex items-center gap-2.5 h-12 rounded-xl border border-slate-200 px-3.5 focus-within:ring-2 focus-within:ring-skybrand">
            <MapPin className="w-4 h-4 text-skybrand shrink-0" />
            <input
              data-testid="search-destination-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSugg(true); }}
              onFocus={() => setShowSugg(true)}
              onBlur={() => { blurTimer.current = setTimeout(() => setShowSugg(false), 150); }}
              placeholder="Where do you want to go?"
              className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          {showSugg && suggestions.length > 0 && (
            <div data-testid="search-destination-suggestions" className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
              {suggestions.map((d) => (
                <button key={d.slug} type="button" data-testid={`search-suggestion-${d.slug}`}
                        onMouseDown={() => { setQuery(d.name); setShowSugg(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sky-50 transition-colors">
                  <img src={d.image} alt="" className="w-8 h-8 rounded-lg object-cover" loading="lazy" />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">{d.name}</span>
                    <span className="block text-xs text-slate-400">{d.country} · {d.region}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="search-date-picker"
                    className="flex items-center gap-2.5 h-12 rounded-xl border border-slate-200 px-3.5 text-sm text-left w-full hover:border-royal transition-colors">
              <CalendarIcon className="w-4 h-4 text-skybrand shrink-0" />
              <span className={range?.from ? "text-slate-800" : "text-slate-400"}>{dateLabel}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 rounded-2xl" align="start" data-lenis-prevent>
            <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="search-travellers-select"
                    className="flex items-center gap-2.5 h-12 rounded-xl border border-slate-200 px-3.5 text-sm text-left w-full hover:border-royal transition-colors">
              <Users className="w-4 h-4 text-skybrand shrink-0" />
              <span className="text-slate-800">{travLabel}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 rounded-2xl p-4" align="start" data-lenis-prevent>
            <Counter label="Adults" sub="12+ years" value={trav.adults} min={1} onChange={(v) => setTrav((t) => ({ ...t, adults: v }))} testid="travellers-adults-count" />
            <Counter label="Children" sub="2 – 11 years" value={trav.children} onChange={(v) => setTrav((t) => ({ ...t, children: v }))} testid="travellers-children-count" />
            <Counter label="Infants" sub="Under 2 years" value={trav.infants} onChange={(v) => setTrav((t) => ({ ...t, infants: v }))} testid="travellers-infants-count" />
          </PopoverContent>
        </Popover>

        <button onClick={submit} data-testid="search-submit-button"
                className="h-12 rounded-xl bg-navy px-7 font-heading text-sm font-bold tracking-wide text-white hover:bg-royal transition-colors flex items-center justify-center gap-2">
          <Search className="w-4 h-4" /> Search
        </button>
      </div>
    </div>
  );
}
